import { ACCESS_CODE_TYPES } from '../../../../enums/AccessCodeEnum';

/**
 * 门禁Code相关工具函数
 */

/**
 * 校验门禁密码格式
 * 规则：只允许数字、*、#，特殊符号（*、#）只能放在首尾，最多8个字符
 * @param code 门禁密码
 * @param t 国际化函数（可选），理论上 key 应该总是存在，fallback 仅作为防御性代码
 * @returns {valid: boolean, message?: string}
 */
export function validateAccessCodeFormat(
    code: string,
    t?: (key: string, params?: { length?: number }) => string
): { valid: boolean; message?: string } {
    // 1. 长度校验
    if (code.length === 0) {
        const message = t 
            ? t('errors.business.accessCode.empty.message')
            : 'Access code cannot be empty'; // Fallback (defensive code, key should always exist)
        return { valid: false, message };
    }

    if (code.length > 8) {
        const message = t
            ? t('errors.business.accessCode.tooLong.message', { length: code.length })
            : 'Access code must be at most 8 characters'; // Fallback (defensive code, key should always exist)
        return { valid: false, message };
    }

    // 2. 字符校验：只能包含数字、字母、*、#
    // 检查每个字符是否都是数字、字母、* 或 #
    for (let i = 0; i < code.length; i++) {
        const char = code[i];
        if (!/[0-9a-zA-Z*#]/.test(char)) {
            const message = t
                ? t('errors.business.accessCode.invalidCharacters.message')
                : 'Access code can only contain digits, letters, *, and #'; // Fallback (defensive code, key should always exist)
            return { valid: false, message };
        }
    }

    // 3. 特殊符号位置校验：*、# 只能放在第一位或最后一位
    // 检查中间部分是否包含特殊符号
    if (code.length > 2) {
        const middle = code.slice(1, -1);
        if (middle.includes('*') || middle.includes('#')) {
            const message = t
                ? t('errors.business.accessCode.specialSymbolsPosition.message')
                : 'Special symbols (*, #) can only be placed at the beginning or end of the password'; // Fallback (defensive code, key should always exist)
            return { valid: false, message };
        }
    }

    // 4. 拦截特定的特殊字符组合：**、##、#*、*#
    const forbiddenPatterns = ['**', '##', '#*', '*#'];
    for (const pattern of forbiddenPatterns) {
        if (code.includes(pattern)) {
            const message = t
                ? t('errors.business.accessCode.forbiddenPatterns.message')
                : 'Password cannot contain these combinations: **, ##, #*, *#'; // Fallback (defensive code, key should always exist)
            return { valid: false, message };
        }
    }

    return { valid: true };
}

/**
 * 门禁类型排序顺序
 * 排序规则：Gate（社区门禁）> MainDoor（楼门门禁）> SideDoor（侧门门禁）> Elevate（电梯门禁）> Garage（车库门禁）> ParcelStop（快递柜）
 */
export const TYPE_SORT_ORDER: Record<string, number> = {
    'Gate': 1,
    'MainDoor': 2,
    'SideDoor': 3,
    'Elevate': 4,
    'Garage': 5,
    'ParcelStop': 6, // 放到最后
    'Other': 999 // 兜底：处理未知类型
};

/**
 * 对门禁Code列表按类型排序（简单排序，不分组）
 * @param codes 门禁Code列表
 * @returns 排序后的列表
 */
export function sortAccessCodesByType<T extends { type: string }>(codes: T[]): T[] {
    return [...codes].sort((a, b) => {
        const orderA = TYPE_SORT_ORDER[a.type] || 999;
        const orderB = TYPE_SORT_ORDER[b.type] || 999;
        return orderA - orderB;
    });
}

/**
 * 门禁Code状态枚举值
 * 0-待验证，1-可用，2-不可用
 */
export enum AccessCodeStatus {
    PENDING_VERIFICATION = 0,
    AVAILABLE = 1,
    UNAVAILABLE = 2
}

/**
 * 门禁Code接口（用于排序和筛选）
 * 与 AccessCode 接口兼容，但允许 null 值用于内部计算
 */
interface AccessCodeForSort {
    id: number;
    type: string;
    code: string;
    status?: number | null; // 状态枚举值：0-待验证，1-可用，2-不可用
    validFeedbackCount?: number;
    invalidFeedbackCount?: number;
    updatedAt?: number | null | undefined;
    createdAt?: number | null | undefined;
    latitude?: number | null | undefined;
    longitude?: number | null | undefined;
}

/**
 * 对门禁Code列表进行分组、筛选和排序
 * 
 * 规则：
 * 1. 按门禁类型分组，类型顺序：Gate（社区门禁）> MainDoor（楼门门禁）> SideDoor（侧门门禁）> Elevate（电梯门禁）> Garage（车库门禁）> ParcelStop（快递柜）
 * 2. 同一类型下：
 *    - 筛选：当前地址定位500m以内的Code
 *    - 排序和筛选：最多展示3条Code
 *      - 第一条：状态可用 > 总计有效反馈数量 > 更新时间
 *      - 第二条：状态待验证 > 更新时间
 *      - 第三条：状态待验证 > 更新时间
 * 
 * @param codes 门禁Code列表
 * @param addressLat 订单地址纬度
 * @param addressLon 订单地址经度
 * @returns 排序和筛选后的列表（按类型分组后展平）
 */
export function filterAndSortAccessCodes(
    codes: AccessCodeForSort[],
    addressLat?: number | null,
    addressLon?: number | null
): AccessCodeForSort[] {
    if (!codes || codes.length === 0) {
        return [];
    }

    // 1. 按类型分组
    const codesByType = codes.reduce((acc, code) => {
        if (!acc[code.type]) {
            acc[code.type] = [];
        }
        acc[code.type].push(code);
        return acc;
    }, {} as Record<string, AccessCodeForSort[]>);

    // 2. 对每个类型进行筛选和排序
    const result: AccessCodeForSort[] = [];
    
    // 按类型顺序处理
    const sortedTypes = Object.keys(codesByType).sort((a, b) => {
        const orderA = TYPE_SORT_ORDER[a] || 999;
        const orderB = TYPE_SORT_ORDER[b] || 999;
        return orderA - orderB;
    });

    for (const type of sortedTypes) {
        const typeCodes = codesByType[type];
        
        // 2.1 筛选500m范围内的Code（如果地址经纬度存在）
        let filteredCodes = typeCodes;
        if (addressLat !== null && addressLat !== undefined && 
            addressLon !== null && addressLon !== undefined) {
            filteredCodes = typeCodes.filter(code => {
                if (code.latitude === null || code.latitude === undefined ||
                    code.longitude === null || code.longitude === undefined) {
                    // 如果Code没有经纬度，保留（允许显示）
                    return true;
                }
                // 计算距离（使用Haversine公式的简化版本）
                const distance = calculateDistance(
                    addressLat,
                    addressLon,
                    code.latitude,
                    code.longitude
                );
                return distance <= 500; // 500m范围内
            });
        }

        // 2.2 按规则排序
        filteredCodes.sort((a, b) => {
            // 第一条：状态可用 > 总计有效反馈数量 > 更新时间
            // 第二条、第三条：状态待验证 > 更新时间
            
            // 状态优先级：1(可用) > 0(待验证) > 2(不可用) > null
            const statusOrder = (status?: number | null) => {
                if (status === AccessCodeStatus.AVAILABLE) return 1; // 可用状态优先级最高
                if (status === AccessCodeStatus.PENDING_VERIFICATION) return 2; // 待验证状态次之
                if (status === AccessCodeStatus.UNAVAILABLE) return 3; // 不可用状态最低
                return 4; // 无状态
            };
            
            const statusA = statusOrder(a.status);
            const statusB = statusOrder(b.status);
            
            if (statusA !== statusB) {
                return statusA - statusB;
            }
            
            // 如果都是可用状态(1)，按有效反馈数量降序
            if (a.status === AccessCodeStatus.AVAILABLE && b.status === AccessCodeStatus.AVAILABLE) {
                const validCountA = a.validFeedbackCount || 0;
                const validCountB = b.validFeedbackCount || 0;
                if (validCountA !== validCountB) {
                    return validCountB - validCountA; // 降序
                }
            }
            
            // 按更新时间降序（最新的在前）
            const updatedAtA = (a.updatedAt ?? a.createdAt) ?? 0;
            const updatedAtB = (b.updatedAt ?? b.createdAt) ?? 0;
            return updatedAtB - updatedAtA; // 降序
        });

        // 2.3 最多取3条
        const selectedCodes = filteredCodes.slice(0, 3);
        result.push(...selectedCodes);
    }

    return result;
}

/**
 * 计算两点之间的距离（Haversine公式）
 * @param lat1 纬度1
 * @param lon1 经度1
 * @param lat2 纬度2
 * @param lon2 经度2
 * @returns 距离（米）
 */
function calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number {
    const R = 6371000; // 地球半径（米）
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * 角度转弧度
 */
function toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
}

/**
 * 获取国际化后的门禁类型名称
 * @param type 门禁类型（如 'Gate', 'MainDoor' 等）
 * @param t 国际化函数
 * @returns 国际化后的类型名称，如果找不到翻译则返回原始类型
 */
export function getLocalizedAccessCodeTypeName(
    type: string,
    t: (key: string) => string
): string {
    const translationKey = `pages.delivery.orderDetail.accessCodeType.${type}`;
    const translated = t(translationKey);
    // 如果翻译结果等于 key 本身，说明没找到翻译，使用 type 作为 fallback
    return translated === translationKey ? type : translated;
}

/**
 * 获取国际化后的门禁类型选项（用于 Picker 组件）
 * @param t 国际化函数
 * @returns 包含 label 和 value 的选项数组
 */
export function getLocalizedAccessCodeTypes(
    t: (key: string) => string
): Array<{ label: string; value: string }> {
    return ACCESS_CODE_TYPES.map(item => ({
        label: t(`pages.delivery.orderDetail.accessCodeType.${item.label}`),
        value: item.value
    }));
}

